import React, { ChangeEvent, useCallback, useMemo, useState, useEffect } from 'react'
import TextArea from 'components/Common/Form/Input/TextArea'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineBurgerIcon from 'LuxKit/Icons/line/LineBurgerIcon'
import LineDiningCreditIcon from 'LuxKit/Icons/line/LineDiningCreditIcon'
import LineFestivalsIcon from 'LuxKit/Icons/line/LineFestivalsIcon'
import LineToasterIcon from 'LuxKit/Icons/line/LineToasterIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { arrayToMap } from 'lib/array/arrayUtils'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import DietaryRequirements from './DietryRequirements'
import * as Analytics from 'analytics/analytics'

const FOOD_STYLES = [
  {
    foodStyle: 'Keep It Simple',
    description: 'Food\'s not my focus.',
    icon: <LineToasterIcon />,
  },
  {
    foodStyle: 'Casual Indulgence',
    description: 'Enjoy food, but not chasing it.',
    icon: <LineBurgerIcon />,
  },
  {
    foodStyle: 'Only the Best',
    description: 'I\'m here for quality.',
    icon: <LineDiningCreditIcon />,
  },
  {
    foodStyle: 'Full-on Foodie',
    description: 'Every meal is an experience.',
    icon: <LineFestivalsIcon />,
  },
]

interface Props {
  hotelCity: string
  foodStyles: Array<{
    foodStyle: string
    selected: boolean
  }>
  dietaryRequirements: Array<{
    dietaryRequirement: string
    selected: boolean
  }>
  onFoodStyleSelect: (foodStyle: string) => void
  onDietaryRequirementSelect: (dietaryRequirement: string) => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripFoodStyleSelection({
  hotelCity,
  foodStyles,
  dietaryRequirements,
  onFoodStyleSelect,
  onDietaryRequirementSelect,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const [customDietaryRequirement, setCustomDietaryRequirement] = useState('')

  const foodStyleMap = useMemo(() =>
    arrayToMap(foodStyles, style => style.foodStyle, style => style.selected),
  [foodStyles])

  const handleDietaryRequirementSelect = useCallback((dietaryRequirement: string) => {
    onDietaryRequirementSelect(dietaryRequirement)
  }, [onDietaryRequirementSelect])

  const handleCustomDietaryRequirementChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setCustomDietaryRequirement(newValue)
    if (newValue.trim()) {
      onDietaryRequirementSelect(`Custom: ${newValue.trim()}`)
    } else {
      onDietaryRequirementSelect('Custom:') // Clear when it's empty
    }
  }, [onDietaryRequirementSelect])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'food_page',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepNumber} of {totalSteps}</BodyText>
        <Heading variant="heading1">
          Now, let’s talk about <i>food</i>
        </Heading>
        <BodyText variant="large">
          How would you like to experience {hotelCity}'s culinary scene?
        </BodyText>
      </VerticalSpacer>

      <Group direction="vertical" gap={16} desktopGap={20}>
        {FOOD_STYLES.map(style => (
          <TileCard
            key={style.foodStyle}
            title={style.foodStyle}
            subtitle={style.description}
            icon={style.icon}
            value={style.foodStyle}
            layout="horizontal"
            onClick={onFoodStyleSelect}
            selected={!!foodStyleMap.get(style.foodStyle)}
            disabled={isGeneratingTrip}
          />))}
      </Group>

      <VerticalSpacer gap={12}>
        <BodyText variant="large">
          <strong>Dietary requirements</strong> (optional)
        </BodyText>
        <DietaryRequirements
          dietaryRequirements={dietaryRequirements}
          onDietaryRequirementSelect={handleDietaryRequirementSelect}
        />
      </VerticalSpacer>

      <VerticalSpacer gap={12}>
        <BodyText variant="large">
          <strong>Any other dietary requirements?</strong> (optional)
        </BodyText>
        <TextArea
          placeholder="e.g., Kosher, dairy-free, nut allergies, keto, low sodium..."
          value={customDietaryRequirement}
          onChange={handleCustomDietaryRequirementChange}
          rows={3}
        />
      </VerticalSpacer>
    </Group>
  )
}

export default AITripFoodStyleSelection
